import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { eventsApi } from "@/api/events";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { JsonViewer } from "@/components/json-viewer";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DetailSkeleton } from "@/components/detail-skeleton";
import { formatAbsolute } from "@/lib/format";
import { useState } from "react";

export const Route = createFileRoute("/_authed/events/$id")({
  component: EventDetailPage,
});

function EventDetailPage() {
  const { id } = Route.useParams();
  const q = useQuery({
    queryKey: ["event", id],
    queryFn: () => eventsApi.get(id),
  });
  const qc = useQueryClient();
  const nav = useNavigate();
  const [replayOpen, setReplayOpen] = useState(false);

  const replay = useMutation({
    mutationFn: () => eventsApi.replay(id),
    onSuccess: (res) => {
      setReplayOpen(false);
      void qc.invalidateQueries({ queryKey: ["runs"] });
      if (res.run_ids.length === 1) {
        void nav({ to: "/runs/$id", params: { id: res.run_ids[0]! } });
      }
    },
  });

  if (q.isLoading) return <DetailSkeleton />;
  if (q.isError || !q.data) return <div className="text-fail">Event not found</div>;

  const ev = q.data;
  return (
    <div className="space-y-6">
      <div>
        <Link to="/events" className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft size={13} /> Events
        </Link>
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-display text-2xl font-semibold tracking-tight font-mono">{ev.name}</h1>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setReplayOpen(true)}>
            <RotateCcw size={14} /> Replay
          </Button>
        </div>
      </div>

      <Card className="grid grid-cols-1 gap-px overflow-hidden bg-border sm:grid-cols-2">
        <div className="bg-card px-4 py-3">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Event ID</div>
          <div className="break-all font-mono text-[13px]">{ev.id}</div>
        </div>
        <div className="bg-card px-4 py-3">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Received</div>
          <div className="font-mono text-[13px]">{formatAbsolute(ev.received_at)}</div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b px-5 py-3">
          <h2 className="font-display text-[15px] font-semibold tracking-tight">Payload</h2>
        </div>
        <div className="p-5"><JsonViewer data={ev.data} /></div>
      </Card>

      {ev.user !== undefined && ev.user !== null && (
        <Card className="overflow-hidden">
          <div className="border-b px-5 py-3">
            <h2 className="font-display text-[15px] font-semibold tracking-tight">User</h2>
          </div>
          <div className="p-5"><JsonViewer data={ev.user} /></div>
        </Card>
      )}

      <ConfirmDialog
        open={replayOpen}
        onOpenChange={setReplayOpen}
        title="Replay this event?"
        description="This will re-run every function matching this event's trigger. New runs will be created and enqueued."
        confirmLabel="Replay event"
        loading={replay.isPending}
        onConfirm={() => replay.mutate()}
      />
    </div>
  );
}
